import { sort } from "@prismicio/editor-support/Array"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { toURL } from "@prismicio/editor-support/URL"
import { z } from "zod"

import type { AuthStrategy } from "../../EditorConfig"
import { languageSchema } from "./document"

const repositoryAuthor = z.object({
  id: z.string(),
  firstname: z.string().optional(),
  lastname: z.string().optional(),
  email: z.string(),
  uploadedAvatar: z.string().optional(),
})

const repositoryAuthors = z.array(repositoryAuthor)

export type RepositoryAuthor = z.TypeOf<typeof repositoryAuthor>

const repositoryQuotas = z.object({
  slicemachineEnabled: z.boolean(),
})

export type RepositoryQuotas = z.TypeOf<typeof repositoryQuotas>

const supportedSliceMachineFrameworks = ["next", "nuxt", "sveltekit"] as const

type SupportedFramework = (typeof supportedSliceMachineFrameworks)[number]

function isSupportedFramework(value: string): value is SupportedFramework {
  return supportedSliceMachineFrameworks.includes(value as SupportedFramework)
}

export const repositoryFramework = z.preprocess(
  (value) => {
    // NOTE: we persist a lot of different frameworks in the DB, but only the SM supported are relevant to us
    // Any other framework is treated like "other"
    if (typeof value === "string" && isSupportedFramework(value)) {
      return value
    }
    return "other"
  },
  z.enum([...supportedSliceMachineFrameworks, "other"]),
)

export type RepositoryFramework = z.TypeOf<typeof repositoryFramework>

const repositorySchema = z
  .object({
    simulator_url: z.string().url().nullish(),
    languages: z
      .array(languageSchema)
      .transform((arr) => sort(arr, (lang) => lang.label)),
    authors: repositoryAuthors.optional().default([]),
    quotas: repositoryQuotas.optional().default({ slicemachineEnabled: false }),
    framework: repositoryFramework.optional().default("other"),
    starterId: z.string().optional(),
    storageVersion: z.object({
      major: z.number().int(),
      minor: z.number().int(),
    }),
    uploadedAvatar: z.string().url().optional(),
  })
  .transform(({ simulator_url, framework, ...rest }) => {
    const simulatorUrl =
      simulator_url && isValidSimulatorURL(simulator_url)
        ? simulator_url
        : undefined

    return {
      ...rest,
      framework,
      simulatorUrl,
      isSupportedFramework: framework !== "other",
    }
  })

export type Repository = z.TypeOf<typeof repositorySchema>

export function getRepository(
  baseUrl: URL,
  authStrategy: AuthStrategy,
): Promise<Repository> {
  return request(new URL("repository", baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: repositorySchema,
  })
}

export function isValidSimulatorURL(urlString: string) {
  const url = toURL(urlString)
  if (!url) return false
  return url.pathname.endsWith("/slice-simulator")
}

const repositoryTagsResponse = z.object({
  results: z.array(z.string()),
})

type RepositoryTags = z.TypeOf<typeof repositoryTagsResponse>

export function getRepositoryTags(
  baseUrl: URL,
  authStrategy: AuthStrategy,
): Promise<RepositoryTags> {
  return request(new URL("repository/tags", baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: repositoryTagsResponse,
  })
}

const repositoryQuotasV2 = z.object({
  maxNbEnvironments: z.number(),
  integrationFieldsEnabled: z.boolean(),
  personalEnvironmentsEnabled: z.boolean(),
  sliceMachineEnabled: z.boolean(),
})

export type RepositoryQuotasV2 = z.TypeOf<typeof repositoryQuotasV2>

export const repositorySchemaV2 = z.object({
  _id: z.string(),
  name: z.string(),
  onboarding: z.object({
    completedSteps: z.array(z.string()),
    isDismissed: z.boolean(),
  }),
  framework: repositoryFramework.optional().default("other"),
  quotas: repositoryQuotasV2.optional().default({
    maxNbEnvironments: 0,
    integrationFieldsEnabled: false,
    personalEnvironmentsEnabled: false,
    sliceMachineEnabled: false,
  }),
  storageVersion: z.object({
    major: z.number().int(),
    minor: z.number().int(),
  }),
  authors: repositoryAuthors.optional().default([]),
})

export type RepositoryV2 = z.infer<typeof repositorySchemaV2>

export function getRepositoryV2(
  baseUrl: URL,
  repository: string,
  authStrategy: AuthStrategy,
): Promise<RepositoryV2> {
  const url = new URL("", baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    headers: { repository },
    credentials: getRequestCredentials(authStrategy),
    okType: repositorySchemaV2,
  })
}
